import React from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tooltip } from '@nextui-org/react';
import { promptStyles } from '../generate-sub/prompt-styles';
import placeholder from '@/assets/images/placeholder.svg';
import { LoraModel } from '@/types/lora';

interface PromptStartersMobileProps {
  sliderRef: React.RefObject<HTMLDivElement>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  setPrompt: (prompt: string) => void;
  setSelectedLoraModel: (model: LoraModel | null) => void;
  productPromptExamples?: any[];
}

export const PromptStartersMobile: React.FC<PromptStartersMobileProps> = ({
  sliderRef,
  textareaRef,
  setPrompt,
  setSelectedLoraModel,
  productPromptExamples = [],
}) => {
  const allPrompts = [
    ...productPromptExamples.map((example) => ({
      name: example.text.slice(0, 20) + '...',
      prompt: example.text,
      image: example.original,
      loraModel: example?.additional_lora_product_id ?? null,
    })),
    ...promptStyles,
  ];

  const handlePromptClick = (style: any) => {
    setPrompt(style.prompt || '');
    if (style.loraModel) {
      setSelectedLoraModel(style.loraModel);
    }

    // Scroll to textarea and focus it
    if (textareaRef.current) {
      // TODO: Add back in?
      // textareaRef.current.scrollIntoView({
      //   behavior: 'smooth',
      //   block: 'center',
      // });
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100); // Add a small delay to ensure smooth scroll completes
    }
  };

  return (
    <div className="mb-0 block xl:hidden">
      <h2 className="mb-0 text-xl font-semibold">Prompt Starters</h2>
      <div className="relative">
        <button
          onClick={() =>
            sliderRef.current?.scrollBy({
              left: -200,
              behavior: 'smooth',
            })
          }
          className="absolute left-0 top-1/2 z-10 -translate-y-1/2 transform rounded-full bg-white p-2 shadow-md"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <div
          ref={sliderRef}
          className="flex space-x-4 overflow-x-auto p-4 scrollbar-hide"
        >
          {allPrompts.map((style, index) => (
            <div
              key={index}
              className="group relative w-24 flex-shrink-0 cursor-pointer text-center"
              onClick={() => handlePromptClick(style)}
            >
              <Image
                src={style.image}
                alt={style.name}
                width={100}
                height={100}
                className="mb-2 h-24 w-24 rounded-md object-cover"
              />
              <p className="text-sm">{style.name}</p>
              {style.loraModel && (
                <Tooltip
                  content={`Uses ${style.loraModel.name} LoRA model`}
                  placement="top"
                >
                  <div className="absolute top-2 right-2 rounded-full bg-black/60 p-1.5 opacity-0 duration-200 transition-opacity group-hover:opacity-100">
                    <Image
                      src={style.loraModel?.image ?? placeholder}
                      alt={style.loraModel.name}
                      width={20}
                      height={20}
                      className="rounded-full"
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={() =>
            sliderRef.current?.scrollBy({ left: 200, behavior: 'smooth' })
          }
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2 transform rounded-full bg-white p-2 shadow-md"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};
